/**
 * Document Formats Configuration and Utilities
 * Supports 16 file formats for document upload
 * Version: 2.0 - Multi-format support
 */

export const FILE_CONSTRAINTS = {
  maxSize: 50 * 1024 * 1024,        // 50 MB for all files
  recommendedImageSize: 20 * 1024 * 1024,  // 20 MB recommended for images
  maxImageDimension: 4096,          // pixels (Gemini API limit)
};

export const SUPPORTED_FORMATS = {
  // Documents Office
  'application/pdf': {
    extension: '.pdf',
    label: 'PDF',
    icon: '📄',
    category: 'document'
  },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extension: '.docx',
    label: 'Word',
    icon: '📝',
    category: 'document'
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    extension: '.pptx',
    label: 'PowerPoint',
    icon: '📊',
    category: 'document',
    multimodal: true  // Automatic analysis
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extension: '.xlsx',
    label: 'Excel',
    icon: '📈',
    category: 'document'
  },

  // Images (all with multimodal analysis)
  'image/jpeg': {
    extension: '.jpg,.jpeg',
    label: 'JPEG',
    icon: '🖼️',
    category: 'image',
    multimodal: true
  },
  'image/png': {
    extension: '.png',
    label: 'PNG',
    icon: '🖼️',
    category: 'image',
    multimodal: true
  },
  'image/gif': {
    extension: '.gif',
    label: 'GIF',
    icon: '🎞️',
    category: 'image',
    multimodal: true
  },
  'image/webp': {
    extension: '.webp',
    label: 'WebP',
    icon: '🖼️',
    category: 'image',
    multimodal: true
  },
  'image/bmp': {
    extension: '.bmp',
    label: 'BMP',
    icon: '🖼️',
    category: 'image',
    multimodal: true
  },
  'image/tiff': {
    extension: '.tiff,.tif',
    label: 'TIFF',
    icon: '🖼️',
    category: 'image',
    multimodal: true
  },

  // Text files
  'text/plain': {
    extension: '.txt',
    label: 'Texte',
    icon: '📃',
    category: 'text'
  },
  'text/csv': {
    extension: '.csv',
    label: 'CSV',
    icon: '📊',
    category: 'text'
  },
  'text/markdown': {
    extension: '.md',
    label: 'Markdown',
    icon: '📝',
    category: 'text'
  },
  'application/rtf': {
    extension: '.rtf',
    label: 'RTF',
    icon: '📝',
    category: 'text'
  }
};

/**
 * Get the accept attribute value for file inputs
 * @returns {string} Comma-separated list of file extensions
 */
export function getAcceptAttribute() {
  return Object.values(SUPPORTED_FORMATS)
    .map(format => format.extension)
    .join(',');
}

/**
 * Get format color by category
 * @param {string} category - Format category (document/image/text)
 * @returns {string} Hex color code
 */
export function getFormatColor(category) {
  const colors = {
    'document': '#2980B9',
    'image': '#9B59B6',
    'text': '#95A5A6'
  };
  return colors[category] || '#34495E';
}

/**
 * Get file information for display
 * @param {File} file - File object
 * @returns {Object} File information
 */
export function getFileInfo(file) {
  const format = SUPPORTED_FORMATS[file.type];
  const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);

  return {
    name: file.name,
    type: format?.label || 'Inconnu',
    icon: format?.icon || '📄',
    category: format?.category || 'unknown',
    size: parseFloat(sizeInMB),
    sizeFormatted: `${sizeInMB} MB`,
    hasMultimodal: format?.multimodal || false,
    extension: file.name.split('.').pop().toUpperCase(),
    color: getFormatColor(format?.category),
    mimeType: file.type
  };
}

/**
 * Validate a file before upload
 * @param {File} file - File to validate
 * @returns {Object} { isValid: boolean, error?: string, warning?: string }
 */
export function validateFile(file) {
  // 1. Check file size
  if (file.size > FILE_CONSTRAINTS.maxSize) {
    return {
      isValid: false,
      error: `Fichier trop volumineux. Maximum : 50 MB (taille actuelle : ${(file.size / 1024 / 1024).toFixed(2)} MB)`
    };
  }

  // 2. Check MIME type
  const supportedTypes = Object.keys(SUPPORTED_FORMATS);
  if (!supportedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Format non supporté : ${file.type}. Formats acceptés : PDF, Word, PowerPoint, Excel, Images (JPG, PNG, GIF, WebP, BMP, TIFF), Texte (TXT, CSV, Markdown, RTF).`
    };
  }

  // 3. Warning for large images
  if (file.type.startsWith('image/') && file.size > FILE_CONSTRAINTS.recommendedImageSize) {
    return {
      isValid: true,
      warning: `L'image est volumineuse (${(file.size / 1024 / 1024).toFixed(2)} MB). Le traitement peut prendre plus de temps.`
    };
  }

  return { isValid: true };
}

/**
 * Calculate estimated processing time based on file type and size
 * @param {File} file - File object
 * @returns {number} Estimated processing time in seconds
 */
export function calculateProcessingTime(file) {
  const sizeInMB = file.size / (1024 * 1024);
  const format = SUPPORTED_FORMATS[file.type];
  let baseTime = 5; // Base processing time in seconds

  if (!format) {
    return baseTime;
  }

  // PDF processing
  if (file.type === 'application/pdf') {
    const estimatedPages = Math.ceil(sizeInMB / 0.05);
    baseTime += estimatedPages * 1.5;

    if (sizeInMB > 2) {
      baseTime += 5; // Add time for potential OCR
    }
  }
  // PowerPoint processing
  else if (file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
    const estimatedSlides = Math.ceil(sizeInMB / 0.1);
    baseTime += estimatedSlides * 2; // 2s per slide (multimodal analysis)
  }
  // Excel processing
  else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    baseTime += sizeInMB * 1.5; // Table extraction
  }
  // Image processing
  else if (format.category === 'image') {
    baseTime += 8; // OCR + multimodal analysis
    if (sizeInMB > 5) {
      baseTime += 5; // Extra time for large images
    }
  }
  // Other formats
  else {
    baseTime += sizeInMB * 0.5;
  }

  // Cap between 5 and 60 seconds
  return Math.min(Math.max(baseTime, 5), 60);
}

/**
 * Get processing stages for progress tracking
 * @param {File} file - File object
 * @returns {Array} Array of {progress: number, message: string}
 */
export function getProcessingStages(file) {
  const format = SUPPORTED_FORMATS[file.type];
  const baseStages = [
    { progress: 15, message: "Téléchargement du fichier..." },
    { progress: 30, message: "Extraction du contenu..." }
  ];

  // Multimodal formats (PPTX, Images)
  if (format?.multimodal) {
    return [
      ...baseStages,
      { progress: 45, message: "Analyse multimodale..." },
      { progress: 60, message: "Extraction des images et graphiques..." },
      { progress: 75, message: "Analyse sémantique..." },
      { progress: 90, message: "Indexation pour la recherche..." },
      { progress: 100, message: "Terminé ! Document prêt." }
    ];
  }

  // Standard formats
  return [
    ...baseStages,
    { progress: 50, message: "Analyse sémantique..." },
    { progress: 70, message: "Identification des concepts..." },
    { progress: 90, message: "Indexation pour la recherche..." },
    { progress: 100, message: "Terminé ! Document prêt." }
  ];
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get format from MIME type
 * @param {string} mimeType - MIME type of the document
 * @returns {Object|null} Format object or null if not found
 */
export function getFormatFromMimeType(mimeType) {
  if (!mimeType) return null;
  return SUPPORTED_FORMATS[mimeType] || null;
}

/**
 * Get format from file name extension
 * @param {string} fileName - File name with extension
 * @returns {Object|null} Format object or null if not found
 */
export function getFormatFromFileName(fileName) {
  if (!fileName) return null;

  const extension = '.' + fileName.split('.').pop().toLowerCase();

  // Search for matching extension in SUPPORTED_FORMATS
  for (const [mimeType, format] of Object.entries(SUPPORTED_FORMATS)) {
    const extensions = format.extension.split(',').map(ext => ext.trim().toLowerCase());
    if (extensions.includes(extension)) {
      return { ...format, mimeType };
    }
  }

  return null;
}

/**
 * Detect format from document metadata
 * @param {Object} metadata - Document metadata object
 * @param {string} fileName - Fallback file name
 * @returns {Object} Format info with mimeType, label, icon, category
 */
export function detectDocumentFormat(metadata, fileName = '') {
  // Priority 1: Use mimeType from metadata
  if (metadata?.mimeType) {
    const format = getFormatFromMimeType(metadata.mimeType);
    if (format) {
      return { ...format, mimeType: metadata.mimeType };
    }
  }

  // Priority 2: Use format field from metadata
  if (metadata?.format) {
    // Try to find MIME type from format string
    const formatLower = metadata.format.toLowerCase();
    for (const [mimeType, format] of Object.entries(SUPPORTED_FORMATS)) {
      if (format.label.toLowerCase() === formatLower ||
          format.extension.includes('.' + formatLower)) {
        return { ...format, mimeType };
      }
    }
  }

  // Priority 3: Detect from fileName
  const fileNameToUse = fileName || metadata?.fileName || '';
  if (fileNameToUse) {
    const format = getFormatFromFileName(fileNameToUse);
    if (format) {
      return format;
    }
  }

  // Default fallback
  return {
    mimeType: 'application/pdf',
    label: 'PDF',
    icon: '📄',
    category: 'document',
    extension: '.pdf'
  };
}

/**
 * Determine how a document should be opened
 * @param {string} mimeType - MIME type of the document
 * @param {string} category - Format category (document/image/text)
 * @returns {string} 'viewer' | 'office-viewer' | 'newtab' | 'download'
 */
export function getDocumentOpenBehavior(mimeType, category) {
  // PDFs open in PDF viewer
  if (mimeType === 'application/pdf') {
    return 'viewer';
  }

  // Office files (Word, Excel, PowerPoint) open in Office viewer
  if (mimeType?.includes('officedocument')) {
    return 'office-viewer';
  }

  // Images open in new tab
  if (category === 'image' || mimeType?.startsWith('image/')) {
    return 'newtab';
  }

  // Text files open in new tab
  if (category === 'text' || mimeType?.startsWith('text/')) {
    return 'newtab';
  }

  // Default: download
  return 'download';
}

/**
 * Check if document should open in viewer
 * @param {string} mimeType - MIME type of the document
 * @returns {boolean} True if should open in viewer
 */
export function shouldOpenInViewer(mimeType) {
  return mimeType === 'application/pdf';
}
